import Styles from 'screens/VirtualMarketerOnboarding/CustomizeSchedule/CustomizeSchedule.module.scss'
import { Chip } from 'components/Chip/Chip'
import { ONLY_WEEK_DAYS, WEEK_DAYS } from 'screens/VirtualMarketerOnboarding/CustomizeSchedule/CustomizeSchedule'
import { Pressable } from '@react-aria/interactions'
import { ChevronDown } from 'blaze-ui/icons'
import { Menu } from 'components/Menu/Menu'
import { useMemo, useState } from 'react'
import { WeekDay } from 'types'
import { Item } from '@react-stately/collections'
import { useRadioGroupState } from '@react-stately/radio'
import { RadioGroupProps } from 'components/RadioGroup/Types'
import { useRadioGroup } from '@react-aria/radio'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import cx from 'classnames'
import { useField } from 'formik'

interface Props extends Omit<RadioGroupProps, 'onChange' | 'value'> {
  onChange: (weekDays: WeekDay[]) => void
  value: WeekDay[]
  chipSize?: 'lg' | 'xs' | 'sm' | 'md'
  chipClassName?: string
}

export const PostDaysRadioGroup = ({ onChange, value, chipClassName, chipSize, className, ...props }: Props) => {
  let radioValue = useMemo(() => {
    if (value.length === WEEK_DAYS.length) return 'everyday'
    if (value.length === ONLY_WEEK_DAYS.length && ONLY_WEEK_DAYS.every((day) => value.includes(day))) return 'weekdays'
    return 'custom'
  }, [value])

  const [selectedDays, setSelectedDays] = useState<Set<WeekDay>>(() => {
    if (radioValue !== 'custom') return new Set<WeekDay>()
    return new Set(value)
  })

  if (selectedDays.size > 0) radioValue = 'custom'

  const state = useRadioGroupState({ ...props, value: radioValue })

  const { radioGroupProps } = useRadioGroup(props, state)

  const chipProps = {
    className: cx(Styles.chip, chipClassName),
    deletable: false,
    variant: 'flat' as const,
    size: chipSize,
    role: 'radio',
  }

  let selectDaysLabel = useMemo(() => {
    let label = 'Let me select days'
    if (state.selectedValue !== 'custom') return label

    if (selectedDays.size === 1) {
      label = [...selectedDays][0]
    } else if (selectedDays.size > 1) {
      label = [...selectedDays].slice(0, -1).join(', ') + ' and ' + [...selectedDays][selectedDays.size - 1]
    }
    return label
  }, [state.selectedValue, selectedDays])

  return (
    <div {...radioGroupProps} className={className}>
      <Chip
        {...chipProps}
        onPress={() => {
          onChange(WEEK_DAYS)
          setSelectedDays(new Set())
        }}
        isSelected={state.selectedValue === 'everyday'}
        aria-checked={state.selectedValue === 'everyday'}
      >
        Everyday
      </Chip>
      <Chip
        {...chipProps}
        onPress={() => {
          onChange(ONLY_WEEK_DAYS)
          setSelectedDays(new Set())
        }}
        isSelected={state.selectedValue === 'weekdays'}
        aria-checked={state.selectedValue === 'weekdays'}
      >
        Weekdays only
      </Chip>
      <DropdownTrigger placement="bottom-start">
        <Pressable>
          <Chip
            {...chipProps}
            isSelected={state.selectedValue === 'custom'}
            aria-checked={state.selectedValue === 'custom'}
          >
            {selectDaysLabel}
            <ChevronDown size={12} />
          </Chip>
        </Pressable>
        <Menu
          onAction={(key) => {
            const newSelectedDays = new Set(selectedDays)
            if (newSelectedDays.has(key as WeekDay)) {
              newSelectedDays.delete(key as WeekDay)
            } else {
              newSelectedDays.add(key as WeekDay)
            }
            setSelectedDays(newSelectedDays)
            onChange([...newSelectedDays])
            if (newSelectedDays.size < 1) {
              onChange(WEEK_DAYS)
            }
          }}
          selectionMode="multiple"
          selectedKeys={selectedDays}
          showCheckmark
          aria-label="Select days"
        >
          {WEEK_DAYS.map((day) => (
            <Item key={day} textValue={day}>
              {day}
            </Item>
          ))}
        </Menu>
      </DropdownTrigger>
    </div>
  )
}

export const FormikPostDaysRadioGroup = (props: Omit<Props, 'onChange' | 'value'> & { name: string }) => {
  const [field, , { setValue }] = useField<WeekDay[]>(props.name)

  return <PostDaysRadioGroup {...props} value={field.value} onChange={setValue} />
}
